import React, { useRef, useState } from "react";
import { HiX, HiCheck } from "react-icons/hi";

interface AddRecoveryLogFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (recoveryLog: {
    selectedRecovery: string[];
    notes: string;
    pointsAmount: number;
    logId?: string;
    logDate?: string;
  }) => Promise<void> | void;
  dateID: string;
  editMode?: boolean;
  initialData?: {
    selectedRecovery: string[];
    notes: string;
    pointsAmount?: number;
  };
  logId?: string;
}

const recoveryMethodsWithPoints = [
  { name: "Meditation", points: 4 },
  { name: "Foam Rolling", points: 3 },
  { name: "Physiotherapy", points: 4 },
  { name: "Recovery Jog", points: 4 },
  { name: "Sauna", points: 4 },
  { name: "Stretching and Mobility", points: 4 },
  { name: "Supplements", points: 1 },
  { name: "Breathing", points: 2 },
  { name: "Contrast Therapy", points: 4 },
  { name: "Compression Therapy", points: 4 },
  { name: "Nap", points: 3 },
  { name: "Grounding", points: 1 },
  { name: "Ice Bath", points: 4 },
  { name: "Massage", points: 5 },
  { name: "Red Light Therapy", points: 4 },
  { name: "Hyperbaric Oxygen Therapy", points: 3 },
  { name: "Cold Showers", points: 2 },
  { name: "Other", points: 1 },
];

const AddRecoveryLogForm: React.FC<AddRecoveryLogFormProps> = ({
  open,
  onClose,
  onSubmit,
  dateID,
  editMode = false,
  initialData,
  logId,
}) => {
  const [selectedRecovery, setSelectedRecovery] = useState<string[]>(
    initialData?.selectedRecovery || [],
  );
  const [notes, setNotes] = useState(initialData?.notes || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInfoDialog, setShowInfoDialog] = useState(false);

  // Convert between a YYYYMMDD dateID and a YYYY-MM-DD <input type="date"> value
  const dateIDToInputValue = (id: string): string => {
    if (!id || id.length < 8) return "";
    return `${id.substring(0, 4)}-${id.substring(4, 6)}-${id.substring(6, 8)}`;
  };
  const inputValueToDateID = (val: string): string => val.replace(/-/g, "");

  const [dateInput, setDateInput] = useState<string>(
    dateIDToInputValue(dateID),
  );
  const dateInputRef = useRef<HTMLInputElement>(null);

  const openDatePicker = () => {
    const el = dateInputRef.current as
      | (HTMLInputElement & { showPicker?: () => void })
      | null;
    if (!el) return;
    try {
      if (typeof el.showPicker === "function") {
        el.showPicker();
      } else {
        el.focus();
      }
    } catch {
      el.focus();
    }
  };

  // Calculate total points based on selected recovery methods
  const calculateTotalPoints = (methods: string[]): number => {
    return methods.reduce((total, method) => {
      const recoveryMethod = recoveryMethodsWithPoints.find(
        (rm) => rm.name === method,
      );
      return total + (recoveryMethod?.points || 0);
    }, 0);
  };

  const totalPoints = calculateTotalPoints(selectedRecovery);

  const formatDate = (dateID: string) => {
    const year = parseInt(dateID.substring(0, 4));
    const month = parseInt(dateID.substring(4, 6));
    const day = parseInt(dateID.substring(6, 8));
    const date = new Date(year, month - 1, day);

    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleSubmit = async () => {
    if (selectedRecovery.length === 0) return;

    setIsSubmitting(true);
    try {
      const logData: {
        selectedRecovery: string[];
        notes: string;
        pointsAmount: number;
        logId?: string;
        logDate?: string;
      } = {
        selectedRecovery,
        notes: notes.trim(),
        pointsAmount: totalPoints,
        logDate: inputValueToDateID(dateInput) || dateID,
      };

      // Only include logId if we're in edit mode and it's defined
      if (editMode && logId) {
        logData.logId = logId;
      }

      await onSubmit(logData);
      handleClose();
    } catch (error) {
      console.error("Error adding recovery log:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (editMode && initialData) {
      setSelectedRecovery(initialData.selectedRecovery);
      setNotes(initialData.notes);
    } else {
      setSelectedRecovery([]);
      setNotes("");
    }
    onClose();
  };

  const prefillKey = initialData
    ? `${initialData.selectedRecovery.join("\u0001")}\n${initialData.notes}`
    : "";
  const prefillRef = useRef(initialData);
  prefillRef.current = initialData;

  // Prefill when the form opens or the assignment changes. Keyed so a parent
  // re-render with a new object of the same content does not wipe the athlete's picks.
  React.useEffect(() => {
    if (!open) return;
    const data = prefillRef.current;
    if (data) {
      setSelectedRecovery(data.selectedRecovery);
      setNotes(data.notes);
    } else if (!editMode) {
      setSelectedRecovery([]);
      setNotes("");
    }
    setDateInput(dateIDToInputValue(dateID));
  }, [editMode, open, dateID, prefillKey]);

  const toggleRecoveryMethod = (method: string) => {
    const alreadySelected = selectedRecovery.includes(method);
    setSelectedRecovery((prev) =>
      alreadySelected
        ? prev.filter((m) => m !== method)
        : [...prev, method],
    );
  };

  const getMethodPoints = (methodName: string): number => {
    const method = recoveryMethodsWithPoints.find(
      (rm) => rm.name === methodName,
    );
    return method?.points || 0;
  };

  if (!open) return null;

  const displayDateID = inputValueToDateID(dateInput) || dateID;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/40 backdrop-blur-sm overflow-y-auto"
      style={{ transform: "translateZ(0)" }}
    >
      <div className="flex min-h-screen md:items-center md:justify-center items-start justify-center">
        <div className="w-full max-w-2xl mx-auto overflow-hidden rounded-none md:rounded-md bg-base-100 text-left align-middle shadow-xl min-h-screen md:min-h-0 md:max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-start p-6 flex-shrink-0">
            <div>
              <div className="flex items-center gap-1">
                <h2 className="text-lg font-grotesk">
                  {editMode ? "Edit Recovery" : "Add Recovery"}
                </h2>
                <button
                  onClick={() => setShowInfoDialog(true)}
                  className="p-1 hover:bg-base-200 rounded-full transition-colors"
                  type="button"
                >
                  <span className="w-4 h-4 flex items-center justify-center text-base-content/50 hover:text-base-content/80 text-xs font-bold">
                    ?
                  </span>
                </button>
              </div>
              {/* Clickable date — opens a native date picker */}
              <div className="relative mt-1">
                <button
                  type="button"
                  onClick={openDatePicker}
                  className="flex items-center gap-1.5 text-sm text-base-content/60 hover:text-base-content transition-colors"
                >
                  <span className="font-medium underline decoration-dotted underline-offset-4">
                    {formatDate(displayDateID)}
                  </span>
                </button>
                <input
                  ref={dateInputRef}
                  type="date"
                  value={dateInput}
                  onChange={(e) => setDateInput(e.target.value)}
                  className="absolute left-0 top-6 w-0 h-0 opacity-0 pointer-events-none"
                  tabIndex={-1}
                  aria-hidden="true"
                />
              </div>
            </div>
            <button
              onClick={handleClose}
              className="bg-base-200 p-2 rounded-full"
            >
              <HiX className="w-5 h-5 text-base-content/80" />
            </button>
          </div>

          {/* Content - Scrollable */}
          <div
            className="flex-1 overflow-y-auto p-4 md:p-6"
            style={{
              WebkitOverflowScrolling: "touch",
              touchAction: "pan-y",
              transform: "translateZ(0)",
              maxHeight: "calc(100vh - 200px)",
              minHeight: "0",
            }}
          >
            {/* Recovery Method */}
            <div className="form-control">
              <div className="gap-y-4 px-4">
                {recoveryMethodsWithPoints
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((method) => (
                      <button
                        key={method.name}
                        type="button"
                        onClick={() => toggleRecoveryMethod(method.name)}
                        className={`flex items-center w-full justify-between p-3 mb-1 rounded-lg border-2 transition-colors ${
                          selectedRecovery.includes(method.name)
                            ? "border-primary bg-primary bg-opacity-10 text-primary"
                            : "border-base-200 hover:border-base-300 text-base-content/80"
                        }`}
                      >
                        <div className="flex items-center justify-between w-full">
                          <span className="text-sm font-medium">
                            {method.name}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs bg-base-200 text-base-content/80 px-2 py-1 rounded-full">
                              +<strong>{method.points}</strong> pts
                            </span>
                            {selectedRecovery.includes(method.name) && (
                              <HiCheck className="w-4 h-4 text-primary" />
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
              </div>
            </div>

            {/* Total Points Display */}
            {selectedRecovery.length > 0 && (
              <div className="mt-4 p-3 bg-base-200 border-base-300 border-2 rounded-lg mx-4">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium font-grotesk text-base-content">
                      Total Recovery Points
                    </span>
                  </div>
                  <span className="text-lg font-bold text-base-content">
                    <strong>{totalPoints}</strong> pts
                  </span>
                </div>
                {/* <div className="mt-2 text-xs text-base-content">
                  Selected: {selectedRecovery.join(", ")}
                </div> */}
              </div>
            )}

            {/* Notes */}
            <div className="form-control pb-4 pt-6 mx-4">
              <textarea
                className="border-base-200 !text-[17px] border-2 p-4 rounded-md bg-base-200 textarea h-40 resize-none focus:outline-none focus:ring-0"
                style={{ lineHeight: "1.1" }}
                placeholder="Add your notes here... (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                autoFocus
              />
            </div>
          </div>

          {/* Footer — floats over the scrolling content with a base→transparent fade */}
          <div className="px-5 pb-5 pt-28 -mt-24 flex-shrink-0 relative z-50 flex items-center justify-end gap-3 bg-gradient-to-t from-base-100 from-30% to-transparent pointer-events-none">
            <button
              onClick={handleSubmit}
              disabled={selectedRecovery.length === 0 || isSubmitting}
              aria-label={editMode ? "Update recovery" : "Log recovery"}
              title={editMode ? "Update recovery" : "Log recovery"}
              className="pointer-events-auto w-14 h-14 rounded-full bg-primary text-white flex items-center justify-center shadow-lg transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <span className="loading loading-spinner loading-sm" />
              ) : (
                <HiCheck className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Information Dialog */}
      {showInfoDialog && (
        <div
          className="fixed inset-0 z-[10000] bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowInfoDialog(false)}
        >
          <div
            className="bg-base-100 rounded-lg max-w-lg w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-bold font-grotesk text-base-content">
                  <span className="font-light">Tracking</span> Recovery
                </h3>
                <button
                  onClick={() => setShowInfoDialog(false)}
                  className="p-1 hover:bg-base-200 rounded-full transition-colors"
                >
                  <HiX className="w-5 h-5 text-base-content/60" />
                </button>
              </div>

              <div className="space-y-4 text-sm text-base-content/80">
                <div>
                  <p className="font-grotesk text-lg">
                    Recovery logging captures the strategies you use outside of
                    training to support how your body and mind respond over
                    time. Recording your recovery practices provides context
                    alongside your training load and wellness, helping you
                    better understand patterns in readiness and response.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-base-content mb-1 font-sans">
                    Active Recovery Methods
                  </h4>
                  <p className="font-sans">
                    Includes activities such as foam rolling, mobility work,
                    stretching, massage, or physiotherapy. These methods utilise
                    physical strategies to support muscle recovery, maintain
                    movement quality, and improve tissue response to training.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-base-content mb-1 font-sans">
                    Mental Recovery
                  </h4>
                  <p className="font-sans">
                    Includes practices such as meditation, breathing exercises,
                    mindfulness, relaxation, or simply enjoyable time away from
                    cognitive demands. Mental recovery supports stress
                    regulation, skill acquisition, and overall mental state
                    between training sessions.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-base-content mb-1 font-sans">
                    Thermal Therapy
                  </h4>
                  <p className="font-sans">
                    Includes methods such as sauna, cold exposure, or contrast
                    bathing. These approaches are commonly used to influence
                    circulation, enhance recovery, and reduce inflammation to
                    improve how the body feels following training or
                    competition.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-base-content mb-1 font-sans">
                    Rest and Sleep
                  </h4>
                  <p className="font-sans">
                    Includes night time sleep and daytime naps. Rest and sleep
                    are essential for physical repair, hormone regulation,
                    nervous system recovery, learning consolidation, and overall
                    readiness.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-base-content mb-1 font-sans">
                    Recovery Points System
                  </h4>
                  <p className="font-sans">
                    Recovery methods are logged using a simple points system
                    that reflects the effectiveness of each recovery activity.
                    Tracking your recovery points helps emphasise recovery
                    behaviour alongside training load, wellness, and readiness,
                    giving you insight into which methods work best for you.
                  </p>
                </div>

                <div className="pt-2 border-t border-base-200">
                  <p className="text-xs text-base-content/80 font-sans italic">
                    Consistent recovery practices are essential for long-term
                    athletic development and injury prevention. If you want to
                    know more about recovery lodge your interest in 'settings'
                    for the "Recovery" module of the PEAK^ Academy.
                  </p>
                </div>
              </div>

              <div className="mt-6">
                <button
                  onClick={() => setShowInfoDialog(false)}
                  className="w-full bg-neutral text-white py-3 px-6 font-grotesk font-bold rounded-md hover:bg-neutral transition-colors"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddRecoveryLogForm;
